import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatTime, formatDate, APPT_TYPE } from "@/lib/constants";
import {
  Calendar as CalendarIcon,
  Inbox,
  ArrowRight,
} from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div
      className="border border-pleiades-border bg-white p-4 rounded-sm"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-pleiades-muted">
          {label}
        </span>
        <Icon className="w-3.5 h-3.5 text-pleiades-muted" strokeWidth={1.6} />
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">
        {value}
      </div>
      {sub && <div className="text-[11px] text-pleiades-slate mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionHeader({ title, cta, to }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-[13px] font-semibold text-pleiades-ink">{title}</h2>
      {cta && (
        <Link
          to={to}
          className="text-[11px] text-pleiades-blue hover:underline flex items-center gap-1"
        >
          {cta} <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [clientsById, setClientsById] = useState({});
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/dashboard/summary"), api.get("/clients")])
      .then(([s, c]) => {
        setData({
          todays_appointments: Array.isArray(s.data?.todays_appointments)
            ? s.data.todays_appointments
            : [],
          recent_applications: Array.isArray(s.data?.recent_applications)
            ? s.data.recent_applications
            : [],
          activity: Array.isArray(s.data?.activity) ? s.data.activity : [],
        });
        const m = {};
        if (Array.isArray(c.data)) {
          c.data.forEach((x) => {
            if (x?.id) m[x.id] = x;
          });
        }
        setClientsById(m);
        setLoadError(false);
      })
      .catch(() => {
        setLoadError(true);
      });
  }, []);

  if (loadError) {
    return (
      <div className="p-3 sm:p-5 lg:p-6" role="alert">
        <div className="border border-pleiades-border bg-white p-5 rounded-sm">
          <h1 className="text-lg font-semibold text-pleiades-ink">
            Yönetim özeti yüklenemedi
          </h1>
          <p className="mt-2 text-[13px] text-pleiades-slate">
            Bağlantıyı kontrol edip sayfayı yeniden deneyin.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-3 sm:p-5 lg:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-24 border border-pleiades-border bg-white/50 animate-pulse rounded-sm"
            />
          ))}
        </div>
      </div>
    );
  }

  const nameOf = (cid) => clientsById[cid]?.full_name || "Danışan";

  return (
    <div className="p-3 sm:p-5 lg:p-6 max-w-[1400px]" data-testid="dashboard-page">
      <div className="mb-3 sm:mb-5 flex items-end justify-between flex-wrap gap-2 sm:gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-pleiades-muted mb-1">
            {new Date().toLocaleDateString("tr-TR", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </div>
          <h1 className="font-editorial text-3xl text-pleiades-ink">Genel Bakış</h1>
          <p className="text-[13px] text-pleiades-slate mt-0.5">
            Bugünün akışı, bekleyen işler ve son gelişmeler.
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 gap-3 mb-3 sm:mb-5">
        <Stat
          label="Bugünkü Randevular"
          value={data.todays_appointments.length}
          sub={
            data.todays_appointments.length > 0
              ? `İlk: ${formatTime(data.todays_appointments[0].starts_at)}`
              : "Randevu yok"
          }
          icon={CalendarIcon}
          testid="stat-today-appointments"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-5">
        {/* Bugün timeline */}
        <div className="lg:col-span-2 border border-pleiades-border bg-white rounded-sm" data-testid="today-timeline">
          <div className="px-4 py-3 border-b border-pleiades-border flex items-center justify-between">
            <div>
              <h2 className="text-[13px] font-semibold">Bugün</h2>
              <p className="text-[11px] text-pleiades-slate">
                Randevu ve seans takvimi
              </p>
            </div>
            <span className="text-[11px] text-pleiades-muted">Bugünün takvimi</span>
          </div>
          <div className="p-2 max-h-[360px] overflow-auto">
            {data.todays_appointments.length === 0 ? (
              <div className="p-8 text-center text-[12px] text-pleiades-slate">
                Bugün için planlanmış randevu bulunmuyor.
              </div>
            ) : (
              data.todays_appointments.map((a) => (
                <Link
                  key={a.id}
                  to={`/danisanlar/${a.client_id}`}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-pleiades-sidebar rounded-sm transition-colors"
                  data-testid={`today-appt-${a.id}`}
                >
                  <div className="w-14 text-right shrink-0">
                    <div className="text-[13px] font-medium tabular-nums">
                      {formatTime(a.starts_at)}
                    </div>
                    <div className="text-[10px] text-pleiades-muted uppercase tracking-wider">
                      {a.duration_minutes}dk
                    </div>
                  </div>
                  <div className="w-px h-8 bg-pleiades-border" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-pleiades-ink truncate">
                      {nameOf(a.client_id)}
                    </div>
                    <div className="text-[11px] text-pleiades-slate">
                      {APPT_TYPE[a.type] || a.type} · {a.location === "online" ? "Online" : "Ofis"}
                    </div>
                  </div>
                  <span
                    className={
                      "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border " +
                      (a.status === "planlandi"
                        ? "text-pleiades-blue border-pleiades-blue/30 bg-pleiades-blue-soft"
                        : "text-pleiades-slate border-pleiades-border bg-pleiades-sidebar")
                    }
                  >
                    {a.status === "planlandi" ? "Planlandı" : a.status}
                  </span>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Recent applications */}
        <div className="border border-pleiades-border bg-white rounded-sm" data-testid="recent-apps">
          <div className="px-4 py-3 border-b border-pleiades-border flex items-center justify-between">
            <div>
              <h2 className="text-[13px] font-semibold">Son Başvurular</h2>
              <p className="text-[11px] text-pleiades-slate">Yeni gelen danışanlar</p>
            </div>
            <Link
              to="/yonetim/danisanlar"
              className="text-[11px] text-pleiades-blue hover:underline flex items-center gap-1"
            >
              Tümü <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="p-1 max-h-[360px] overflow-auto">
            {data.recent_applications.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-pleiades-slate">
                Yeni başvuru yok.
              </div>
            ) : (
              data.recent_applications.map((c) => (
                <Link
                  key={c.id}
                  to={`/danisanlar/${c.id}`}
                  className="flex items-start gap-2.5 px-3 py-2 hover:bg-pleiades-sidebar rounded-sm transition-colors"
                  data-testid={`app-item-${c.id}`}
                >
                  <div className="w-7 h-7 rounded-sm bg-pleiades-blue-soft text-pleiades-blue text-[11px] flex items-center justify-center font-medium shrink-0 mt-0.5">
                    <Inbox className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate">
                      {c.full_name}
                    </div>
                    <div className="text-[11px] text-pleiades-slate truncate">
                      {c.city} · {formatDate(c.created_at)}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Activity */}
      <div>
        <div className="border border-pleiades-border bg-white rounded-sm p-4" data-testid="activity-chart">
          <SectionHeader title="Son 30 Gün — Randevu Aktivitesi" />
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.activity} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="#F0EDE7" strokeDasharray="0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#94A3B8" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E2DC" }}
                  tickFormatter={(v) => v.slice(8, 10)}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#94A3B8" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E2DC" }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    border: "1px solid #E5E2DC",
                    borderRadius: 4,
                    padding: "6px 8px",
                  }}
                  labelFormatter={(v) => new Date(v).toLocaleDateString("tr-TR")}
                  formatter={(v) => [v, "Randevu"]}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3B5998"
                  strokeWidth={1.5}
                  dot={{ fill: "#3B5998", r: 2 }}
                  activeDot={{ r: 4, fill: "#C5A059" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

    </div>
  );
}
